import type { IMessage } from '@/types/admin/messages';

/**
 * Determines if a message was sent by the current user.
 * 
 * Logic:
 * - Primary: Compare sender.id with authUserId (as numbers to handle type coercion from API)
 * - Fallback: If no valid comparison possible, use isAdmin flag (admin messages = not from you)
 * 
 * @param senderId - The sender's account ID from the message
 * @param authUserId - The authenticated user's ID
 * @param isAdmin - Whether the message was sent by an admin
 * @returns true if the message was sent by the current user
 */
export const isMessageSentByUser = (
  senderId: number | string | undefined | null,
  authUserId: number | string | undefined | null,
  isAdmin: boolean
): boolean => {
  const hasSenderId = senderId != null;
  const hasAuthUserId = authUserId != null;
  
  if (hasSenderId && hasAuthUserId) {
    return Number(senderId) === Number(authUserId);
  }
  
  // Fallback: if no valid comparison possible, use isAdmin flag
  // Admin messages are not from the current user (resident)
  return !isAdmin;
};

/**
 * Helper to check if a message was sent by the current user from a full IMessage object
 */
export const isMessageFromCurrentUser = (
  message: IMessage,
  authUserId: number | string | undefined | null
): boolean => {
  return isMessageSentByUser(message.sender?.id, authUserId, message.isAdmin);
};

/**
 * Formats the sender name for display in message bubbles
 * 
 * @param message - The message object
 * @param sentByYou - Whether the message was sent by the current user
 * @param variant - 'mobile' shows 'You', 'desktop' shows 'Sent by you'
 * @returns Formatted sender name string
 */
export const formatSenderName = (
  message: IMessage,
  sentByYou: boolean,
  variant: 'mobile' | 'desktop' = 'mobile'
): string => {
  if (sentByYou) {
    return variant === 'mobile' ? 'You' : 'Sent by you';
  }
  
  if (message.sender?.firstName?.length) {
    const fullName = `${message.sender.firstName} ${message.sender.lastName || ''}`.trim();
    return message.isAdmin ? `${fullName} (admin)` : fullName;
  }
  
  return message.sender?.email || '';
};

export default {
  isMessageSentByUser,
  isMessageFromCurrentUser,
  formatSenderName,
};
